/**
 * OnboardingGate — Multi-stage animated onboarding experience.
 *
 * Nests inside ProtectedRoute after NDASigningGate:
 *   ProtectedRoute → NDASigningGate → OnboardingGate → AppShell
 *
 * Stages:
 *   1. Welcome — animated intro to Felix and the platform
 *   2. Preference Chat — conversational 30-question personality profiler
 *   3. Summary — user-friendly preference description + confirmation
 *   4. Workspace — Google Workspace OAuth offer
 *   5. Celebration — confetti + "You're all set!"
 *
 * Motion design follows `docs/ONBOARDING_RESEARCH.md`:
 *   - Direction-aware slides between stages (SPRING_CONFIDENT 400/35)
 *   - Stagger 40ms for content children (STAGGER_ENERGETIC)
 *   - Optimistic progress — advances on intent (§4 Principle 6)
 *   - One earned celebration at the final stage (§4 Principle 8)
 *   - Parallel reduced-motion art direction via useReducedMotion()
 *   - 3D tilt + spring on hover for feature cards (§3 micro-interactions)
 *   - Circular progress ring on chat stage (tactile beats a thin bar)
 *   - Mouse-parallax on background shapes (depth without distraction)
 *   - Keyboard shortcuts 1-5 for scale answers, Esc to skip
 */

import { useState, useEffect, useCallback, useRef, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AnimatePresence,
  m,
  useMotionValue,
  useSpring,
  useTransform,
  useReducedMotion,
} from "framer-motion";
import { onboardingApi } from "@/api/services";
import { useModalA11y, dialogA11yProps } from "@/hooks/useModalA11y";
import { FelixDog } from "@/components/FelixDog";
import {
  FelixAvatar,
  FELIX_AVATAR_VARIANTS,
  type FelixAvatarVariant,
} from "@/components/meeting/FelixAvatar";
import { useSetFelixAvatar } from "@/api/felixAvatarApi";
import {
  ONBOARDING_ITEMS,
  SCALE_LABELS,
  ROUND_TRANSITIONS,
  TRAIT_DESCRIPTIONS,
} from "@/config/onboardingQuestions";
import type {
  OnboardingItemResponse,
  OnboardingPreferenceSummary,
} from "@/api/types";
import {
  Sparkles,
  Rocket,
  Shield,
  MessageSquare,
  CheckCircle2,
  ChevronRight,
  Bot,
  Globe,
  Mail,
  CalendarDays,
  Contact,
  HardDrive,
  ExternalLink,
} from "lucide-react";
import {
  SPRING_CONFIDENT,
  SPRING_PLAYFUL,
  STAGGER_ENERGETIC,
  STAGGER_CONSIDERED,
  EASE_EMERGE,
  chooseSlideVariants,
  chooseTransition,
} from "@/components/motion";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Stage = "welcome" | "chat" | "summary" | "avatar" | "workspace" | "celebration";

// Linear-ordered list used to compute slide direction. OB-01 inserts the
// avatar-pick stage after "summary" and before "workspace".
const STAGE_ORDER: Stage[] = ["welcome", "chat", "summary", "avatar", "workspace", "celebration"];

interface ChatMessage {
  role: "felix" | "user";
  text: string;
  scaleResponse?: number;
}

// ---------------------------------------------------------------------------
// Feature highlight cards for Welcome stage
// ---------------------------------------------------------------------------

const FEATURES = [
  {
    icon: Sparkles,
    title: "Plan with AI",
    desc: "Objectives, stories, requirements — Felix helps you define what to build.",
    color: "text-purple-300",
    bg: "from-purple-500/30 to-purple-500/10",
  },
  {
    icon: Shield,
    title: "Build with Confidence",
    desc: "Tasks, components, conflict detection — catch issues before they ship.",
    color: "text-emerald-300",
    bg: "from-emerald-500/30 to-emerald-500/10",
  },
  {
    icon: CheckCircle2,
    title: "Ship with Quality",
    desc: "Test cases, documents, change control — every release is production-ready.",
    color: "text-blue-300",
    bg: "from-blue-500/30 to-blue-500/10",
  },
  {
    icon: MessageSquare,
    title: "Always by Your Side",
    desc: "Felix is available in the sidebar whenever you need guidance.",
    color: "text-amber-300",
    bg: "from-amber-500/30 to-amber-500/10",
  },
];

// ---------------------------------------------------------------------------
// Acknowledgments
// ---------------------------------------------------------------------------

const ACKNOWLEDGMENTS = [
  "Got it!",
  "Thanks for sharing that.",
  "Noted!",
  "That's helpful to know.",
  "Great, I'll remember that.",
  "Understood.",
  "Good to know!",
  "Thanks — that tells me a lot.",
  "Perfect.",
  "Appreciate you sharing that!",
];

// ---------------------------------------------------------------------------
// FeatureCard — 3D mouse-tilt + spring entrance
// ---------------------------------------------------------------------------

function FeatureCard({
  feature,
  reduceMotion,
}: {
  feature: typeof FEATURES[number];
  reduceMotion: boolean;
}) {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useTransform(y, [-50, 50], [8, -8]);
  const rotateY = useTransform(x, [-50, 50], [-8, 8]);
  const springX = useSpring(rotateX, { stiffness: 300, damping: 30 });
  const springY = useSpring(rotateY, { stiffness: 300, damping: 30 });

  function handleMouseMove(e: React.MouseEvent<HTMLDivElement>) {
    if (reduceMotion) return;
    const rect = e.currentTarget.getBoundingClientRect();
    x.set(e.clientX - rect.left - rect.width / 2);
    y.set(e.clientY - rect.top - rect.height / 2);
  }

  function handleMouseLeave() {
    x.set(0);
    y.set(0);
  }

  return (
    <m.div
      variants={{
        hidden: { opacity: 0, y: 16 },
        show: { opacity: 1, y: 0, transition: SPRING_CONFIDENT },
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={
        reduceMotion
          ? undefined
          : { rotateX: springX, rotateY: springY, transformPerspective: 800 }
      }
      className="group flex items-start gap-3 p-4 rounded-xl bg-white/10 backdrop-blur-sm border border-white/10 text-left hover:bg-white/15 hover:border-white/25 transition-colors will-change-transform"
    >
      <div className={`p-2 rounded-lg bg-gradient-to-br ${feature.bg} shrink-0 ring-1 ring-white/10`}>
        <feature.icon className={`h-5 w-5 ${feature.color}`} />
      </div>
      <div>
        <h3 className="font-semibold text-white text-sm">{feature.title}</h3>
        <p className="text-white/65 text-xs mt-0.5 leading-relaxed">{feature.desc}</p>
      </div>
    </m.div>
  );
}

// ---------------------------------------------------------------------------
// CircularProgress — replaces the thin bar on chat stage
// ---------------------------------------------------------------------------

function CircularProgress({
  current,
  total,
}: {
  current: number;
  total: number;
}) {
  const pct = Math.min(current / total, 1);
  const r = 18;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - pct);

  return (
    <div className="relative h-12 w-12">
      <svg className="h-12 w-12 -rotate-90" viewBox="0 0 48 48">
        <circle
          cx="24" cy="24" r={r}
          stroke="currentColor"
          strokeWidth="3"
          fill="none"
          className="text-slate-200 dark:text-slate-700"
        />
        <m.circle
          cx="24" cy="24" r={r}
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          fill="none"
          className="text-brand-500"
          strokeDasharray={c}
          animate={{ strokeDashoffset: offset }}
          transition={SPRING_CONFIDENT}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center text-[10px] font-medium tabular-nums text-slate-700 dark:text-slate-200">
        {Math.min(current, total)}/{total}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ParallaxBackground — depth without distraction
// ---------------------------------------------------------------------------

function ParallaxBackground({ reduceMotion }: { reduceMotion: boolean }) {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const springMX = useSpring(mouseX, { stiffness: 60, damping: 20 });
  const springMY = useSpring(mouseY, { stiffness: 60, damping: 20 });

  useEffect(() => {
    if (reduceMotion) return;
    function onMove(e: MouseEvent) {
      const { innerWidth, innerHeight } = window;
      mouseX.set((e.clientX / innerWidth - 0.5) * 30);
      mouseY.set((e.clientY / innerHeight - 0.5) * 30);
    }
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [reduceMotion, mouseX, mouseY]);

  const shapes = [
    { top: "10%", left: "5%", size: 128, depth: 1.0 },
    { top: "60%", right: "8%", size: 96, depth: 0.7 },
    { bottom: "15%", left: "15%", size: 64, depth: 1.2 },
    { top: "30%", right: "20%", size: 80, depth: 0.5 },
  ];

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      {shapes.map((s, i) => (
        <m.div
          key={i}
          className="absolute rounded-full bg-white/5 animate-onboarding-float"
          style={{
            top: s.top,
            left: s.left,
            right: s.right,
            bottom: s.bottom,
            width: s.size,
            height: s.size,
            animationDelay: `${i}s`,
            x: reduceMotion ? 0 : useTransform(springMX, (v) => v * s.depth),
            y: reduceMotion ? 0 : useTransform(springMY, (v) => v * s.depth),
          }}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ParticleConfetti — physics-based, one earned moment only
// ---------------------------------------------------------------------------

const CONFETTI_COLORS = [
  "#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899",
];

function ParticleConfetti({ count = 60 }: { count?: number }) {
  // Pre-compute particle randomness so it's stable across renders.
  const particles = useRef<Array<{
    x: number; rotate: number; color: string; size: number; duration: number; delay: number;
  }>>(
    Array.from({ length: count }).map(() => ({
      x: (Math.random() - 0.5) * 800,
      rotate: Math.random() * 720 - 360,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      size: 6 + Math.random() * 6,
      duration: 1.2 + Math.random() * 1.6,
      delay: Math.random() * 0.3,
    })),
  );

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
      {particles.current.map((p, i) => (
        <m.div
          key={i}
          initial={{ x: 0, y: 0, opacity: 1, rotate: 0 }}
          animate={{
            x: p.x,
            y: window.innerHeight * 0.9,
            opacity: [1, 1, 0],
            rotate: p.rotate,
          }}
          transition={{
            duration: p.duration,
            delay: p.delay,
            ease: [0.32, 0.0, 0.67, 1.0], // gravity-like easing
          }}
          style={{
            position: "absolute",
            left: "50%",
            top: "30%",
            width: p.size,
            height: p.size,
            backgroundColor: p.color,
            borderRadius: 2,
          }}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface OnboardingGateProps {
  children: ReactNode;
}

export function OnboardingGate({ children }: OnboardingGateProps) {
  const queryClient = useQueryClient();
  const reduceMotion = !!useReducedMotion();
  const slideVariants = chooseSlideVariants(reduceMotion);
  const slideTransition = chooseTransition(reduceMotion);

  // Fetch onboarding status
  const { data: status, isLoading } = useQuery({
    queryKey: ["onboarding-status"],
    queryFn: onboardingApi.getStatus,
    staleTime: 60_000,
    retry: 1,
  });

  // Local state
  const [stage, setStage] = useState<Stage>("welcome");
  const [prevStageIdx, setPrevStageIdx] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [responses, setResponses] = useState<OnboardingItemResponse[]>([]);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [preferenceSummary, setPreferenceSummary] = useState<OnboardingPreferenceSummary[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const [celebrationTimer, setCelebrationTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  // OB-01 — avatar pick. Defaults to "orb" (the original look) so leaving the
  // stage without an explicit change still persists a valid, unchanged choice.
  const [avatarVariant, setAvatarVariant] = useState<FelixAvatarVariant>("orb");
  const setFelixAvatar = useSetFelixAvatar();
  // Row 250b5312 — NEVER FAIL SILENTLY. Any failed onboarding action (complete,
  // skip, or "Enter FTL" finding the status still incomplete) sets this, and
  // the gate renders a visible message with a retry, "Skip for now", and a
  // "Continue to FTL" that lets the user in for this session regardless.
  const [gateError, setGateError] = useState<
    { kind: "complete" | "skip" | "enter"; message: string } | null
  >(null);
  const [bypassed, setBypassed] = useState(false);

  const stageIdx = STAGE_ORDER.indexOf(stage);
  const direction = stageIdx > prevStageIdx ? 1 : -1;

  function gotoStage(next: Stage) {
    setPrevStageIdx(STAGE_ORDER.indexOf(stage));
    setStage(next);
  }

  // localStorage backup for mid-flow refresh
  const STORAGE_KEY = "ftl-onboarding-progress";

  // Restore progress from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.responses?.length > 0) {
          setResponses(parsed.responses);
          setCurrentQuestion(parsed.currentQuestion || 0);
          setStage("chat");
          setPrevStageIdx(0);
        }
      }
    } catch {
      // ignore parse errors
    }
  }, []);

  // Save progress to localStorage
  useEffect(() => {
    if (responses.length > 0) {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ responses, currentQuestion })
      );
    }
  }, [responses, currentQuestion]);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  // Cleanup celebration timer
  useEffect(() => {
    return () => {
      if (celebrationTimer) clearTimeout(celebrationTimer);
    };
  }, [celebrationTimer]);

  // Add Felix's opening message when entering chat stage
  useEffect(() => {
    if (stage === "chat" && chatMessages.length === 0) {
      const roundTransition = ROUND_TRANSITIONS[1];
      setChatMessages([
        {
          role: "felix",
          text: "Hey there! Before we dive in, I'd love to learn a little about how you like to work so I can tailor our interactions to your style.",
        },
        { role: "felix", text: roundTransition },
        { role: "felix", text: `"${ONBOARDING_ITEMS[0].text}"` },
      ]);
    }
  }, [stage, chatMessages.length]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleSkip = useCallback(async () => {
    setGateError(null);
    try {
      await onboardingApi.skip();
      localStorage.removeItem(STORAGE_KEY);
      queryClient.invalidateQueries({ queryKey: ["onboarding-status"] });
    } catch {
      setGateError({
        kind: "skip",
        message:
          "We couldn't record that you skipped setup. Try again, or continue to FTL for now.",
      });
    }
  }, [queryClient]);

  // Submit the questionnaire. On failure the user stays on the chat stage
  // with a visible message, a retry and a skip — never a silent advance.
  const submitResponses = useCallback(
    async (all: OnboardingItemResponse[]) => {
      setIsSubmitting(true);
      setGateError(null);
      try {
        const result = await onboardingApi.complete({
          responses: all,
          skipped: false,
        });
        setPreferenceSummary(result.summary || []);
        gotoStage("summary");
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        setGateError({
          kind: "complete",
          message:
            "We couldn't save your answers. Try again, or skip for now — you can take the assessment later from Profile → AI Preferences.",
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [],  // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleScaleResponse = useCallback(
    async (score: number) => {
      const item = ONBOARDING_ITEMS[currentQuestion];
      const newResponse: OnboardingItemResponse = { item_id: item.id, score };
      const updatedResponses = [...responses, newResponse];
      setResponses(updatedResponses);

      const ack = ACKNOWLEDGMENTS[currentQuestion % ACKNOWLEDGMENTS.length];
      const userMsg: ChatMessage = {
        role: "user",
        text: SCALE_LABELS[score],
        scaleResponse: score,
      };

      const nextQ = currentQuestion + 1;

      if (nextQ >= ONBOARDING_ITEMS.length) {
        setChatMessages((prev) => [
          ...prev,
          userMsg,
          { role: "felix", text: `${ack} That's everything I needed — let me put together your profile...` },
        ]);
        setCurrentQuestion(nextQ);
        await submitResponses(updatedResponses);
        return;
      }

      const currentRound = item.roundNum;
      const nextRound = ONBOARDING_ITEMS[nextQ].roundNum;
      const isNewRound = nextRound !== currentRound;

      const newMessages: ChatMessage[] = [userMsg, { role: "felix", text: ack }];
      if (isNewRound) {
        newMessages.push({ role: "felix", text: ROUND_TRANSITIONS[nextRound] });
      }
      newMessages.push({ role: "felix", text: `"${ONBOARDING_ITEMS[nextQ].text}"` });

      setChatMessages((prev) => [...prev, ...newMessages]);
      setCurrentQuestion(nextQ);
    },
    [currentQuestion, responses, submitResponses],
  );

  // Keyboard shortcuts on the chat stage
  useEffect(() => {
    if (stage !== "chat" || isSubmitting) return;
    function onKey(e: KeyboardEvent) {
      // 1-5 → select scale answer
      if (e.key >= "1" && e.key <= "5") {
        const score = Number(e.key);
        if (score >= 1 && score <= 5) {
          e.preventDefault();
          void handleScaleResponse(score);
        }
      }
      // Esc → skip
      if (e.key === "Escape") {
        e.preventDefault();
        void handleSkip();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [stage, isSubmitting, handleScaleResponse, handleSkip]);

  const handleSummaryConfirm = useCallback(() => gotoStage("avatar"), []);  // eslint-disable-line react-hooks/exhaustive-deps

  // OB-01 — persist the chosen Felix avatar variant, then advance. The PUT is
  // best-effort: onboarding must never stall on a settings write, so we advance
  // regardless (the optimistic cache already reflects the pick, and "orb" is a
  // safe default if the write never lands).
  const handleAvatarContinue = useCallback(async () => {
    try {
      await setFelixAvatar.mutateAsync(avatarVariant);
    } catch {
      /* non-blocking — advance anyway */
    }
    gotoStage("workspace");
  }, [avatarVariant, setFelixAvatar]);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleWorkspaceComplete = useCallback(() => {
    gotoStage("celebration");
    const timer = setTimeout(() => {
      localStorage.removeItem(STORAGE_KEY);
      queryClient.invalidateQueries({ queryKey: ["onboarding-status"] });
    }, 3500);
    setCelebrationTimer(timer);
  }, [queryClient]);  // eslint-disable-line react-hooks/exhaustive-deps

  // "Enter FTL" (and "Skip for now" after the answers are saved). It re-reads
  // the status; if the server still says not completed, the user sees a
  // message and a way forward instead of a button that does nothing
  // (row 250b5312: measured on production, the button re-read False forever).
  const handleEnterApp = useCallback(async () => {
    localStorage.removeItem(STORAGE_KEY);
    if (celebrationTimer) clearTimeout(celebrationTimer);
    setGateError(null);
    try {
      const fresh = await queryClient.fetchQuery({
        queryKey: ["onboarding-status"],
        queryFn: onboardingApi.getStatus,
        staleTime: 0,
      });
      if (fresh && fresh.completed && !fresh.needs_retake) return;
    } catch {
      /* fall through to the visible message */
    }
    setGateError({
      kind: "enter",
      message:
        "Your setup hasn't finished saving, so FTL couldn't open yet. Try again, skip for now, or continue to FTL.",
    });
  }, [queryClient, celebrationTimer]);

  const retryFailedAction = useCallback(() => {
    if (!gateError) return;
    if (gateError.kind === "complete") void submitResponses(responses);
    else if (gateError.kind === "skip") void handleSkip();
    else void handleEnterApp();
  }, [gateError, responses, submitResponses, handleSkip, handleEnterApp]);

  // ---------------------------------------------------------------------------
  // Gate logic
  // ---------------------------------------------------------------------------

  const gateActive =
    !bypassed && !isLoading && !!status && !(status.completed && !status.needs_retake);

  // Focus-trap + focus-restore + scroll-lock while the gate blocks the app.
  // Escape (skip) is handled by the stage-level key handler above, so the
  // hook's Escape is disabled to avoid double-firing.
  const gateRef = useModalA11y<HTMLDivElement>({
    open: gateActive,
    onClose: handleSkip,
    dismissable: false,
  });

  if (bypassed) return <>{children}</>;
  if (isLoading) return <>{children}</>;
  if (!status) return <>{children}</>;
  if (status.completed && !status.needs_retake) return <>{children}</>;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div
      ref={gateRef}
      {...dialogA11yProps({ label: "Welcome onboarding" })}
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-gradient-to-br from-brand-600 via-brand-700 to-brand-900 dark:from-gray-900 dark:via-gray-800 dark:to-brand-900 overflow-hidden"
    >
      <ParallaxBackground reduceMotion={reduceMotion} />

      {gateError && (
        <div
          role="alert"
          data-testid="onboarding-error"
          className="absolute top-4 left-1/2 -translate-x-1/2 z-20 w-[min(92vw,36rem)] rounded-xl bg-white dark:bg-slate-900 border border-red-300 dark:border-red-800 shadow-xl px-4 py-3 text-left"
        >
          <p className="text-sm text-red-700 dark:text-red-300">{gateError.message}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={retryFailedAction}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-brand-600 text-white hover:bg-brand-700"
            >
              Try again
            </button>
            {gateError.kind !== "skip" && (
              <button
                type="button"
                onClick={() => void handleSkip()}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200"
              >
                Skip for now
              </button>
            )}
            <button
              type="button"
              onClick={() => setBypassed(true)}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200"
            >
              Continue to FTL
            </button>
          </div>
        </div>
      )}

      <AnimatePresence mode="wait" custom={direction}>
        {/* ─── Welcome Stage ─── */}
        {stage === "welcome" && (
          <m.div
            key="welcome"
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={slideTransition}
            className="relative z-10 flex flex-col items-center max-w-3xl mx-auto px-6 text-center"
          >
            <m.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1.5, opacity: 1 }}
              transition={{ ...SPRING_PLAYFUL, delay: 0.1 }}
              className="mb-6"
            >
              <FelixDog state="idle" label="" />
            </m.div>

            <m.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...SPRING_CONFIDENT, delay: 0.3 }}
            >
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-3 tracking-tight">
                Hi, I'm Felix!
              </h1>
              <p className="text-lg md:text-xl text-white/80 mb-8 max-w-xl mx-auto leading-relaxed">
                Your AI Program Manager. I help you build software — from idea to delivery.
              </p>
            </m.div>

            <m.div
              variants={{
                hidden: {},
                show: { transition: STAGGER_ENERGETIC },
              }}
              initial="hidden"
              animate="show"
              className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10 w-full"
              style={{ transformStyle: "preserve-3d" }}
            >
              {FEATURES.map((f) => (
                <FeatureCard key={f.title} feature={f} reduceMotion={reduceMotion} />
              ))}
            </m.div>

            <m.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.4, ease: EASE_EMERGE }}
              className="flex flex-col items-center gap-3"
            >
              <m.button
                onClick={() => gotoStage("chat")}
                whileHover={reduceMotion ? undefined : { scale: 1.03 }}
                whileTap={reduceMotion ? undefined : { scale: 0.97 }}
                transition={SPRING_CONFIDENT}
                className="px-8 py-3 bg-white text-brand-700 font-semibold rounded-xl shadow-lg hover:shadow-xl hover:bg-white/95 flex items-center gap-2"
              >
                Let's Get Started
                <ChevronRight className="h-4 w-4" />
              </m.button>
              <button
                onClick={handleSkip}
                data-testid="onboarding-skip-welcome"
                className="text-sm text-white/50 hover:text-white/80 transition-colors underline underline-offset-2"
              >
                Skip for now
              </button>
              <p className="text-xs text-white/50">
                You can take it later from Profile → AI Preferences.
              </p>
            </m.div>
          </m.div>
        )}

        {/* ─── Chat Stage ─── */}
        {stage === "chat" && (
          <m.div
            key="chat"
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={slideTransition}
            className="relative z-10 flex flex-col w-full max-w-2xl h-[90vh] mx-auto bg-white dark:bg-slate-900 rounded-2xl shadow-2xl overflow-hidden"
          >
            <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40">
              <div className="p-1.5 rounded-lg bg-brand-100 dark:bg-brand-900/30">
                <Bot className="h-5 w-5 text-brand-600" />
              </div>
              <div className="flex-1">
                <h2 className="font-semibold text-slate-900 dark:text-slate-100 text-sm">
                  Setting up your preferences
                </h2>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Press <kbd className="px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-[10px] font-mono">1</kbd>–<kbd className="px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-[10px] font-mono">5</kbd> to answer
                </p>
              </div>
              <CircularProgress
                current={Math.min(currentQuestion + 1, ONBOARDING_ITEMS.length)}
                total={ONBOARDING_ITEMS.length}
              />
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4" aria-live="polite">
              <AnimatePresence initial={false}>
                {chatMessages.map((msg, i) => (
                  <m.div
                    key={i}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={SPRING_CONFIDENT}
                    className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                        msg.role === "user"
                          ? "bg-brand-500 text-white rounded-br-md"
                          : "bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-bl-md"
                      }`}
                    >
                      {msg.text}
                    </div>
                  </m.div>
                ))}
              </AnimatePresence>

              {isSubmitting && (
                <m.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex justify-start"
                >
                  <div className="px-4 py-2.5 rounded-2xl bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 text-sm flex items-center gap-2">
                    <span className="animate-pulse">Analyzing your preferences...</span>
                  </div>
                </m.div>
              )}

              <div ref={chatEndRef} />
            </div>

            {currentQuestion < ONBOARDING_ITEMS.length && !isSubmitting && (
              <div className="px-5 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40">
                <m.div
                  variants={{ hidden: {}, show: { transition: STAGGER_ENERGETIC } }}
                  initial="hidden"
                  animate="show"
                  className="flex flex-wrap justify-center gap-2"
                >
                  {Object.entries(SCALE_LABELS).map(([score, label]) => (
                    <m.button
                      key={score}
                      variants={{
                        hidden: { opacity: 0, y: 6 },
                        show: { opacity: 1, y: 0, transition: SPRING_CONFIDENT },
                      }}
                      whileHover={reduceMotion ? undefined : { scale: 1.04 }}
                      whileTap={reduceMotion ? undefined : { scale: 0.96 }}
                      onClick={() => handleScaleResponse(Number(score))}
                      className="relative px-4 py-2 text-sm rounded-full border border-slate-200 dark:border-slate-700 hover:border-brand-400 hover:bg-brand-50 dark:hover:bg-brand-900/20 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-brand-400 focus:ring-offset-1"
                    >
                      <span className="absolute -top-1.5 -left-1.5 h-4 w-4 rounded-full bg-slate-200 dark:bg-slate-700 text-[10px] font-mono leading-4 text-slate-600 dark:text-slate-300">
                        {score}
                      </span>
                      {label}
                    </m.button>
                  ))}
                </m.div>
              </div>
            )}
            {!isSubmitting && (
              <div className="text-center py-3 bg-slate-50 dark:bg-slate-800/40">
                <button
                  onClick={handleSkip}
                  data-testid="onboarding-skip-chat"
                  className="text-xs text-slate-500 dark:text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                >
                  Skip for now <kbd className="ml-1 px-1 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-[9px] font-mono">Esc</kbd>
                </button>
              </div>
            )}
          </m.div>
        )}

        {/* ─── Summary Stage ─── */}
        {stage === "summary" && (
          <m.div
            key="summary"
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={slideTransition}
            className="relative z-10 flex flex-col items-center max-w-lg mx-auto px-6 text-center"
          >
            <m.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1.25, opacity: 1 }}
              transition={SPRING_PLAYFUL}
              className="mb-4"
            >
              <FelixDog state="idle" label="" />
            </m.div>
            <h2 className="text-2xl font-bold text-white mb-2">
              Here's how I'll work with you
            </h2>
            <p className="text-white/60 text-sm mb-6">
              Based on your preferences, here's how I'll adapt:
            </p>

            <m.div
              variants={{ hidden: {}, show: { transition: STAGGER_CONSIDERED } }}
              initial="hidden"
              animate="show"
              className="w-full space-y-3 mb-8"
            >
              {(preferenceSummary.length > 0
                ? preferenceSummary.map((item) => ({
                    key: item.trait_name,
                    name: item.trait_name,
                    desc: item.description,
                  }))
                : Object.entries(TRAIT_DESCRIPTIONS).map(([trait, desc]) => ({
                    key: trait,
                    name: desc.name,
                    desc: desc.moderate,
                  }))
              ).map((item) => (
                <m.div
                  key={item.key}
                  variants={{
                    hidden: { opacity: 0, x: -12 },
                    show: { opacity: 1, x: 0, transition: SPRING_CONFIDENT },
                  }}
                  className="flex items-start gap-3 p-3 rounded-xl bg-white/10 backdrop-blur-sm border border-white/10 text-left"
                >
                  <m.div
                    initial={{ rotate: -45, scale: 0 }}
                    animate={{ rotate: 0, scale: 1 }}
                    transition={SPRING_PLAYFUL}
                  >
                    <Sparkles className="h-4 w-4 text-amber-300 mt-0.5 shrink-0" />
                  </m.div>
                  <div>
                    <p className="text-white font-medium text-sm">{item.name}</p>
                    <p className="text-white/65 text-xs mt-0.5 leading-relaxed">{item.desc}</p>
                  </div>
                </m.div>
              ))}
            </m.div>

            <m.button
              onClick={handleSummaryConfirm}
              whileHover={reduceMotion ? undefined : { scale: 1.03 }}
              whileTap={reduceMotion ? undefined : { scale: 0.97 }}
              className="px-8 py-3 bg-white text-brand-700 font-semibold rounded-xl shadow-lg hover:shadow-xl flex items-center gap-2"
            >
              Sounds right!
              <CheckCircle2 className="h-4 w-4" />
            </m.button>
            <button
              type="button"
              onClick={() => void handleEnterApp()}
              data-testid="onboarding-skip-summary"
              className="mt-3 px-4 py-2 text-sm text-white/60 hover:text-white/80 transition-colors"
            >
              Skip for now
            </button>
          </m.div>
        )}

        {/* ─── Avatar Stage (OB-01) ─── */}
        {stage === "avatar" && (
          <m.div
            key="avatar"
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={slideTransition}
            className="relative z-10 flex flex-col items-center max-w-2xl mx-auto px-6 text-center"
          >
            <m.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={SPRING_PLAYFUL}
              className="mb-2"
            >
              <Sparkles className="h-8 w-8 text-amber-300 mx-auto" />
            </m.div>
            <h2 className="text-2xl font-bold text-white mb-2">
              Give Felix a look
            </h2>
            <p className="text-white/60 text-sm mb-8 max-w-md mx-auto">
              Pick the avatar style you'd like to see Felix wear across your
              meetings. You can change this later in settings.
            </p>

            <m.div
              variants={{ hidden: {}, show: { transition: STAGGER_ENERGETIC } }}
              initial="hidden"
              animate="show"
              className="grid grid-cols-3 sm:grid-cols-5 gap-4 mb-10 w-full"
            >
              {FELIX_AVATAR_VARIANTS.map((v) => {
                const selected = avatarVariant === v.id;
                return (
                  <m.button
                    key={v.id}
                    type="button"
                    variants={{
                      hidden: { opacity: 0, y: 10 },
                      show: { opacity: 1, y: 0, transition: SPRING_CONFIDENT },
                    }}
                    whileHover={reduceMotion ? undefined : { scale: 1.05 }}
                    whileTap={reduceMotion ? undefined : { scale: 0.96 }}
                    onClick={() => setAvatarVariant(v.id)}
                    aria-pressed={selected}
                    aria-label={`Felix avatar: ${v.label}`}
                    className={`flex flex-col items-center gap-2 p-3 rounded-2xl border transition-colors ${
                      selected
                        ? "bg-white/20 border-white/60 ring-2 ring-white/70"
                        : "bg-white/10 border-white/10 hover:bg-white/15 hover:border-white/30"
                    }`}
                  >
                    <FelixAvatar
                      variant={v.id}
                      state="listening"
                      size="lg"
                      showLabel={false}
                    />
                    <span className="text-xs font-medium text-white/85">
                      {v.label}
                    </span>
                  </m.button>
                );
              })}
            </m.div>

            <m.button
              onClick={handleAvatarContinue}
              disabled={setFelixAvatar.isPending}
              whileHover={reduceMotion ? undefined : { scale: 1.03 }}
              whileTap={reduceMotion ? undefined : { scale: 0.97 }}
              className="px-8 py-3 bg-white text-brand-700 font-semibold rounded-xl shadow-lg hover:shadow-xl flex items-center gap-2 disabled:opacity-70"
            >
              {setFelixAvatar.isPending ? "Saving…" : "Continue"}
              <ChevronRight className="h-4 w-4" />
            </m.button>
            <button
              type="button"
              onClick={() => void handleEnterApp()}
              data-testid="onboarding-skip-avatar"
              className="mt-3 px-4 py-2 text-sm text-white/60 hover:text-white/80 transition-colors"
            >
              Skip for now
            </button>
          </m.div>
        )}

        {/* ─── Workspace Stage ─── */}
        {stage === "workspace" && (
          <m.div
            key="workspace"
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={slideTransition}
            className="relative z-10 flex flex-col items-center max-w-lg mx-auto px-6 text-center"
          >
            <div className="mb-6">
              <m.div
                initial={{ rotate: -10, scale: 0.8 }}
                animate={{ rotate: 0, scale: 1 }}
                transition={SPRING_PLAYFUL}
                className="h-16 w-16 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center mx-auto mb-4"
              >
                <Globe className="h-8 w-8 text-white" />
              </m.div>
              <h2 className="text-2xl font-bold text-white mb-2">Connect Google Workspace</h2>
              <p className="text-white/70 text-sm max-w-sm mx-auto leading-relaxed">
                Link your Google account to access Gmail, Calendar, Contacts, and Drive directly within FTL.
              </p>
            </div>

            <m.div
              variants={{ hidden: {}, show: { transition: STAGGER_ENERGETIC } }}
              initial="hidden"
              animate="show"
              className="grid grid-cols-2 gap-3 mb-6 w-full max-w-sm"
            >
              {[
                { icon: Mail, label: "Gmail", desc: "Inbox management" },
                { icon: CalendarDays, label: "Calendar", desc: "Schedule & availability" },
                { icon: Contact, label: "Contacts", desc: "Directory search" },
                { icon: HardDrive, label: "Drive", desc: "File management" },
              ].map(({ icon: Icon, label, desc }) => (
                <m.div
                  key={label}
                  variants={{
                    hidden: { opacity: 0, y: 8 },
                    show: { opacity: 1, y: 0, transition: SPRING_CONFIDENT },
                  }}
                  className="rounded-xl bg-white/10 backdrop-blur p-3 text-left"
                >
                  <Icon className="h-5 w-5 text-white/80 mb-1.5" />
                  <p className="text-sm font-medium text-white">{label}</p>
                  <p className="text-[11px] text-white/60">{desc}</p>
                </m.div>
              ))}
            </m.div>

            <div className="flex flex-col gap-2 w-full max-w-xs">
              <m.button
                whileHover={reduceMotion ? undefined : { scale: 1.03 }}
                whileTap={reduceMotion ? undefined : { scale: 0.97 }}
                onClick={async () => {
                  try {
                    const { oauthApi } = await import("@/api/services");
                    const result = await oauthApi.connect("google");
                    const authUrl = (result as unknown as Record<string, string>)?.authorization_url;
                    if (authUrl) {
                      window.open(authUrl, "google-oauth", "width=600,height=700");
                      const listener = (e: MessageEvent) => {
                        if (e.data?.type === "oauth_complete") {
                          window.removeEventListener("message", listener);
                          handleWorkspaceComplete();
                        }
                      };
                      window.addEventListener("message", listener);
                    }
                  } catch {
                    /* silently continue */
                  }
                }}
                className="px-8 py-3 bg-white text-brand-700 font-semibold rounded-xl shadow-lg hover:shadow-xl flex items-center gap-2 justify-center"
              >
                <ExternalLink className="h-4 w-4" />
                Connect Google
              </m.button>
              <button
                onClick={handleWorkspaceComplete}
                data-testid="onboarding-skip-workspace"
                className="px-4 py-2 text-sm text-white/60 hover:text-white/80 transition-colors"
              >
                Skip for now
              </button>
            </div>
          </m.div>
        )}

        {/* ─── Celebration Stage ─── */}
        {stage === "celebration" && (
          <m.div
            key="celebration"
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={slideTransition}
            className="relative z-10 flex flex-col items-center max-w-lg mx-auto px-6 text-center"
          >
            {!reduceMotion && <ParticleConfetti count={60} />}

            <m.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1.5, rotate: 0 }}
              transition={{ ...SPRING_PLAYFUL, delay: 0.1 }}
              className="mb-4"
            >
              <FelixDog state="walking" label="" />
            </m.div>

            <m.h2
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...SPRING_CONFIDENT, delay: 0.4 }}
              className="text-3xl font-bold text-white mb-2"
            >
              You're all set!
            </m.h2>
            <m.p
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...SPRING_CONFIDENT, delay: 0.55 }}
              className="text-white/70 text-sm mb-8"
            >
              I've tailored my communication style to match your preferences. Let's build something great together.
            </m.p>

            <m.button
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ ...SPRING_CONFIDENT, delay: 0.7 }}
              whileHover={reduceMotion ? undefined : { scale: 1.04 }}
              whileTap={reduceMotion ? undefined : { scale: 0.97 }}
              onClick={() => void handleEnterApp()}
              className="px-8 py-3 bg-white text-brand-700 font-semibold rounded-xl shadow-lg hover:shadow-xl flex items-center gap-2"
            >
              <Rocket className="h-4 w-4" />
              Enter FTL
            </m.button>
            <button
              type="button"
              onClick={() => void handleEnterApp()}
              data-testid="onboarding-skip-celebration"
              className="mt-3 px-4 py-2 text-sm text-white/60 hover:text-white/80 transition-colors"
            >
              Skip for now
            </button>
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
}
